import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FiX, FiInfo, FiHeart, FiBookmark, FiAlertCircle, FiPlus, FiShield, FiCheckCircle, FiXCircle, FiClock, FiMessageSquare, FiTrash2, FiUser, FiZap, FiChevronDown } from 'react-icons/fi';
import { FaMagic, FaCamera } from 'react-icons/fa';
import { apiRequest, apiUpload } from '../utils/auth';
import { getEffectiveExtensionStatus } from '../utils/extensionStatus';
import { getLensAccessState, type LensAccessState } from '../utils/lensAccessState';
import {
  GENERATION_START_TIMEOUT_MS,
  GenerationStartResponse,
  pollGenerationUntilDone,
} from '../utils/generationPolling';
import { useEscapeToClose } from '../utils/useEscapeToClose';
import { lockAppScroll } from '../utils/scrollLock';
import { useCursorFeed } from '../hooks/useCursorFeed';
import LazyListFooter from './LazyListFooter';
import './ExtensionDetailsModal.css';

/** Default `availableTiers` for an extension: every paid plan, no free access. Mirrors Backend DEFAULT_AVAILABLE_TIERS. */
const DEFAULT_AVAILABLE_TIERS = ['basic', 'pro', 'premium'];

const TIER_OPTIONS: { value: string; label: string; desc: string }[] = [
  { value: 'free', label: 'Free', desc: 'Available to free users' },
  { value: 'basic', label: 'Basic', desc: 'Available to Basic subscribers' },
  { value: 'pro', label: 'Pro', desc: 'Available to Pro subscribers' },
  { value: 'premium', label: 'Premium', desc: 'Available to Premium subscribers' },
];


/** Plan id → display label. Unknown/absent plans read as Free. */
const PLAN_LABELS: Record<string, string> = {
  free: 'Free',
  basic: 'Basic',
  pro: 'Pro',
  premium: 'Premium',
};


interface SampleImage {
  inputImages: string[];       // presigned display URLs
  outputImage: string;         // presigned display URL
  inputImageKeys?: string[];   // raw GCS bucket keys
  outputImageKey?: string;     // raw GCS bucket key
}

interface LightboxPhoto {
  url: string;
  label: string;      // "Enhanced" or "Image 1", "Image 2", ...
  isEnhanced: boolean;
}

// Ordered list for the lightbox: enhanced result first, then the original inputs.
const buildLightboxPhotos = (sample: SampleImage | null): LightboxPhoto[] => {
  if (!sample) return [];
  const photos: LightboxPhoto[] = [];
  if (sample.outputImage) photos.push({ url: sample.outputImage, label: 'Enhanced', isEnhanced: true });
  (sample.inputImages || []).forEach((url, i) => {
    if (url) photos.push({ url, label: `Image ${i + 1}`, isEnhanced: false });
  });
  return photos;
};

interface Category {
  _id: string;
  category_id: string;
  label: { en: string; 'zh-hk'?: string };
  order: number;
}

interface Extension {
  _id: string;
  Name: string;
  Creator: string;
  creatorName?: string;
  prompt: string;
  CreditCost: number;
  order: number;
  thumbnailImageUrl?: string;
  ReferenceImage?: string;        // legacy raw GCS key (first reference)
  ReferenceImageUrl?: string;     // legacy presigned display URL (first reference)
  ReferenceImages?: string[];     // raw GCS keys
  ReferenceImageUrls?: string[];  // presigned display URLs
  aiModel?: string;
  official?: boolean;
  availableTiers?: string[];
  requiredPhotoCount?: number;
  isDynamicInput?: boolean;
  categories?: string[];
  keywords?: string[];
  aspectRatio?: string;
  sampleImagesCount: number;
  sampleImages?: SampleImage[];
  likeCount: number;
  bookmarkCount: number;
  usageCount?: number;
  reportStatus?: 'Active' | 'Suspended' | 'inReview';
  isPublished?: boolean;
  isArchived?: boolean;
  isPublic?: boolean;
  isDeleted?: boolean;
  description?: string;
  imageRequirements?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface ImageModelOption {
  _id: string;
  /** Identity — stored on Extension.aiModel */
  model_id: string;
  /** Model id sent to the provider; several entries may share one */
  model_name: string;
  display_name?: string;
  credit: string;
  enabled?: boolean;
}

const LENS_ACCESS_HINTS: Record<LensAccessState, string> = {
  draft: 'Not published; hidden from users.',
  private: 'Published for the creator only.',
  public: 'Published for public access.',
  archived: 'Hidden from the store and unavailable.',
};

interface StatusHistoryItem {
  status: string;
  time: string;
  reason?: string;
}

interface ExtensionReport {
  _id: string;
  userId: string;
  reporterName: string;
  reporterEmail: string | null;
  reason: string;
  additionalInfo: string | null;
  createdAt: string;
}

type TabType = 'details' | 'engagement' | 'reports';

type EngagementType = 'liked' | 'bookmarked' | 'used';

interface EngagementUser {
  userId: string;
  name: string;
  email?: string;
  profilePicture?: string;
  plan?: string;
  isCreator?: boolean;
  usageCount?: number;
}

interface ExtensionDetailsModalProps {
  extension: Extension;
  isOpen: boolean;
  onClose: () => void;
  onExtensionUpdated?: (updated: Extension) => void;
  onStatusChanged?: () => void;
  onExtensionDeleted?: (extensionId: string) => void;
}

const ASPECT_RATIOS = ['1:1', '2:3', '3:2', '3:4', '4:3', '16:9', '9:16', 'original'] as const;

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode; bgColor: string }> = {
  active: { label: 'Suspended', color: '#e74c3c', icon: <FiXCircle />, bgColor: '#3a1a1a' },
  inactive: { label: 'Unsuspended', color: '#27ae60', icon: <FiCheckCircle />, bgColor: '#1a3a1a' },
  appeal_pending: { label: 'Appeal Submitted', color: 'var(--color-warning-strong)', icon: <FiMessageSquare />, bgColor: '#3a2a1a' },
  appeal_approved: { label: 'Appeal Approved', color: '#27ae60', icon: <FiCheckCircle />, bgColor: '#1a3a1a' },
  appeal_rejected: { label: 'Appeal Rejected', color: '#e74c3c', icon: <FiXCircle />, bgColor: '#3a1a1a' },
  resubmit_pending: { label: 'Resubmission Pending', color: 'var(--color-accent-alt)', icon: <FiClock />, bgColor: '#1a2a3a' },
  resubmit_approved: { label: 'Resubmission Approved', color: '#27ae60', icon: <FiCheckCircle />, bgColor: '#1a3a1a' },
  resubmit_rejected: { label: 'Resubmission Rejected', color: '#e74c3c', icon: <FiXCircle />, bgColor: '#3a1a1a' },
};

// Human-readable labels for the report reasons stored in ReportExtensionLog.
const REPORT_REASON_LABELS: Record<string, string> = {
  inappropriate_content: 'Inappropriate content',
  spam: 'Spam',
  copyright_violation: 'Copyright',
  misleading: 'Misleading',
  harassment: 'Harassment',
  other: 'Other',
};

const reportReasonLabel = (reason: string): string => REPORT_REASON_LABELS[reason] || reason;

export default function ExtensionDetailsModal({ extension, isOpen, onClose, onExtensionUpdated, onStatusChanged, onExtensionDeleted }: ExtensionDetailsModalProps) {
  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [savedSettings, setSavedSettings] = useState(extension);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Editable fields
  const [name, setName] = useState(extension.Name);
  const [prompt, setPrompt] = useState(extension.prompt);
  const [description, setDescription] = useState(extension.description || '');
  const [imageRequirements, setImageRequirements] = useState(extension.imageRequirements || '');
  const [aiModel, setAiModel] = useState(extension.aiModel || '');
  const [creditCost, setCreditCost] = useState(extension.CreditCost);
  const [aspectRatio, setAspectRatio] = useState(extension.aspectRatio || 'original');
  const [official, setOfficial] = useState(extension.official ?? false);
  const [availableTiers, setAvailableTiers] = useState<string[]>(extension.availableTiers || [...DEFAULT_AVAILABLE_TIERS]);

  // Image models from DB
  const [imageModels, setImageModels] = useState<ImageModelOption[]>([]);

  // Sample images
  const [sampleImages, setSampleImages] = useState<SampleImage[]>([]);
  const [draggedImageIdx, setDraggedImageIdx] = useState<number | null>(null);
  const [samplesDirty, setSamplesDirty] = useState(false);

  // Reference images (guide generation — sent to the AI ahead of the user's photos).
  // Keys and URLs are index-aligned. Falls back to the legacy single fields.
  const [referenceImageKeys, setReferenceImageKeys] = useState<string[]>(
    extension.ReferenceImages && extension.ReferenceImages.length > 0
      ? extension.ReferenceImages
      : (extension.ReferenceImage ? [extension.ReferenceImage] : [])
  );
  const [referenceImageUrls, setReferenceImageUrls] = useState<string[]>(
    extension.ReferenceImageUrls && extension.ReferenceImageUrls.length > 0
      ? extension.ReferenceImageUrls
      : (extension.ReferenceImageUrl ? [extension.ReferenceImageUrl] : [])
  );
  const [referenceUploading, setReferenceUploading] = useState(false);

  // Add new sample image
  const [addSampleInputFiles, setAddSampleInputFiles] = useState<File[]>([]);
  const [addSampleInputPreviews, setAddSampleInputPreviews] = useState<string[]>([]);
  const [addSampleGenerating, setAddSampleGenerating] = useState(false);
  const [addSampleGeneratedPreview, setAddSampleGeneratedPreview] = useState<string | null>(null);
  const [addSampleGeneratedKeys, setAddSampleGeneratedKeys] = useState<{ originalImageKeys: string[]; enhancedImageKey: string } | null>(null);
  const [addSampleError, setAddSampleError] = useState<string | null>(null);
  const [showAddSampleModal, setShowAddSampleModal] = useState(false);
  const [addSampleSaving, setAddSampleSaving] = useState(false);

  // Categories
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(extension.categories || []);
  const [draggedCatIdx, setDraggedCatIdx] = useState<number | null>(null);

  // Keywords
  const [keywords, setKeywords] = useState<string[]>(extension.keywords || []);
  const [keywordInput, setKeywordInput] = useState('');

  // Report & Suspend tab
  const [statusHistory, setStatusHistory] = useState<StatusHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [currentReportStatus, setCurrentReportStatus] = useState(extension.reportStatus || 'Active');
  const [suspendReason, setSuspendReason] = useState('');
  const [respondReason, setRespondReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // User reports filed against this extension (who reported, why, how many)
  const [reports, setReports] = useState<ExtensionReport[]>([]);
  const [totalReports, setTotalReports] = useState(0);
  const [reasonBreakdown, setReasonBreakdown] = useState<Record<string, number>>({});
  const [reportsLoading, setReportsLoading] = useState(false);

  // isDynamicInput toggle
  const [isDynamicInput, setIsDynamicInput] = useState(extension.isDynamicInput ?? false);

  // Publication status
  const [isPublished, setIsPublished] = useState(extension.isPublished ?? true);

  // Archive status
  const [isArchived, setIsArchived] = useState(extension.isArchived ?? false);

  // Visibility
  const [isPublic, setIsPublic] = useState(extension.isPublic ?? false);

  const lensAccessState = getLensAccessState({ isPublished, isArchived, isPublic });

  const handleAccessStateChange = (value: string) => {
    if (value === 'archived') {
      // Retain the publication and visibility flags while taking the lens offline.
      setIsArchived(true);
    } else if (value === 'draft') {
      setIsPublished(false);
      setIsArchived(false);
    } else if (value === 'public' || value === 'private') {
      setIsPublished(true);
      setIsArchived(false);
      setIsPublic(value === 'public');
    }
  };

  // Delete
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Sample image lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxSample, setLightboxSample] = useState<SampleImage | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Usage count (confirmed generations) — loaded from the detail endpoint
  const [usageCount, setUsageCount] = useState<number | null>(extension.usageCount ?? null);

  // Engagement users modal (likes / bookmarks / usages)
  const [engagementModal, setEngagementModal] = useState<{ type: EngagementType; title: string } | null>(null);
  const engagementType = engagementModal?.type;
  const engagementScrollRef = useRef<HTMLDivElement>(null);
  const engagementDialogRef = useRef<HTMLDivElement>(null);
  const engagementFeed = useCursorFeed<EngagementUser>(isOpen && engagementType
    ? `/api/admin/extensions/${extension._id}/engagement-users?type=${engagementType}&limit=25` : null, 'userId');
  const engagementUsers = engagementFeed.items;
  const engagementLoading = engagementFeed.loading;
  const engagementError = engagementFeed.error;
  const engagementTotal = engagementFeed.response?.pagination?.total ?? engagementUsers.length;
  useEffect(() => (isOpen ? lockAppScroll() : undefined), [isOpen]);
  useEffect(() => {
    if (!engagementType) return;
    const trigger = document.activeElement;
    const dialog = engagementDialogRef.current;
    dialog?.querySelector<HTMLButtonElement>('button')?.focus();
    const trapFocus = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || !dialog) return;
      const buttons = Array.from(dialog.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'));
      const first = buttons[0]; const last = buttons[buttons.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', trapFocus);
    return () => { document.removeEventListener('keydown', trapFocus); if (trigger instanceof HTMLElement && trigger.isConnected) trigger.focus(); };
  }, [engagementType]);

  // Change Creator
  const [creatorSearch, setCreatorSearch] = useState('');
  const [creatorResults, setCreatorResults] = useState<{ userId: string; name: string; email: string; avatar?: string }[]>([]);
  const [creatorSearching, setCreatorSearching] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState<{ userId: string; name: string } | null>(null);
  const [creatorSaving, setCreatorSaving] = useState(false);
  const [creatorSaveMsg, setCreatorSaveMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [currentCreatorId, setCurrentCreatorId] = useState(extension.Creator);
  const [currentCreatorName, setCurrentCreatorName] = useState(extension.creatorName || extension.Creator);

  useEffect(() => {
    setEngagementModal(null);
    if (!isOpen) return;
    loadExtensionDetail();
    loadCategories();
    loadImageModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, extension._id]);

  useEffect(() => {
    if (!isOpen || activeTab !== 'reports') return;
    loadSuspendHistory();
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, extension._id]);

  // Escape dismisses one layer at a time — declared outermost first so the
  // sub-modals and the lightbox sit above this modal on the dismiss stack.
  useEscapeToClose(isOpen, onClose);
  // Closing is blocked while a generation/save is in flight (same guard as the
  // backdrop click); swallowing the key stops it reaching the modal behind.
  useEscapeToClose(showAddSampleModal, () => {
    if (!addSampleGenerating && !addSampleSaving) setShowAddSampleModal(false);
  });
  useEscapeToClose(!!engagementModal, () => setEngagementModal(null));
  useEscapeToClose(lightboxOpen, () => setLightboxOpen(false));

  // Arrow-key navigation for the sample image lightbox (Escape is handled above)
  useEffect(() => {
    if (!lightboxOpen) return;
    const count = buildLightboxPhotos(lightboxSample).length;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setLightboxIndex(i => (i + 1) % count);
      else if (e.key === 'ArrowLeft') setLightboxIndex(i => (i - 1 + count) % count);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, lightboxSample]);

  const loadExtensionDetail = async () => {
    try {
      setLoading(true);
      const res = await apiRequest<Extension & { success: boolean }>(`/api/admin/extensions/${extension._id}`);
      if (res.success) {
        setSavedSettings(res);
        setSampleImages(res.sampleImages || []);
        setReferenceImageKeys(
          res.ReferenceImages && res.ReferenceImages.length > 0
            ? res.ReferenceImages
            : (res.ReferenceImage ? [res.ReferenceImage] : [])
        );
        setReferenceImageUrls(
          res.ReferenceImageUrls && res.ReferenceImageUrls.length > 0
            ? res.ReferenceImageUrls
            : (res.ReferenceImageUrl ? [res.ReferenceImageUrl] : [])
        );
        setPrompt(res.prompt);
        setDescription(res.description || '');
        setImageRequirements(res.imageRequirements || '');
        setAiModel(res.aiModel || '');
        setAspectRatio(res.aspectRatio || 'original');
        setOfficial(res.official ?? false);
        setAvailableTiers(res.availableTiers || [...DEFAULT_AVAILABLE_TIERS]);
        setSelectedCategories(res.categories || []);
        setKeywords(res.keywords || []);
        setCurrentReportStatus(res.reportStatus || 'Active');
        setIsDynamicInput(res.isDynamicInput ?? false);
        setIsPublished(res.isPublished ?? true);
        setIsArchived(res.isArchived ?? false);
        setIsPublic(res.isPublic ?? false);
        setUsageCount(res.usageCount ?? 0);
      }
    } catch (err) {
      console.error('Failed to load extension detail', err);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const res = await apiRequest<{ success: boolean; data: Category[] }>('/api/admin/categories?limit=200');
      if (res.success && res.data) setAllCategories(res.data);
    } catch (err) {
      console.error('Failed to load categories', err);
    }
  };

  const loadImageModels = async () => {
    try {
      const res = await apiRequest<{ success: boolean; data: ImageModelOption[] }>('/api/admin/image-models');
      if (res.success && res.data) setImageModels(res.data);
    } catch (err) {
      console.error('Failed to load image models', err);
    }
  };

  const loadSuspendHistory = async () => {
    try {
      setHistoryLoading(true);
      const res = await apiRequest<{
        success: boolean;
        extensionId: string;
        statusHistory: StatusHistoryItem[];
      }>(`/api/admin/extensions/${extension._id}/suspend-history`);
      if (res.success) {
        setStatusHistory(res.statusHistory || []);
      }
    } catch (err) {
      console.error('Failed to load suspend history', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadReports = async () => {
    try {
      setReportsLoading(true);
      const res = await apiRequest<{
        success: boolean;
        totalReports: number;
        reasonBreakdown: Record<string, number>;
        reports: ExtensionReport[];
      }>(`/api/admin/reports/extensions/${extension._id}?limit=100`);
      if (res.success) {
        setReports(res.reports || []);
        setTotalReports(res.totalReports || 0);
        setReasonBreakdown(res.reasonBreakdown || {});
      }
    } catch (err) {
      console.error('Failed to load extension reports', err);
    } finally {
      setReportsLoading(false);
    }
  };

  // Extensions store the model's identity (model_id)
  const findModel = (value: string) => imageModels.find(m => m.model_id === value);

  const selectedModel = findModel(aiModel);
  const selectedModelAvailable = Boolean(selectedModel && selectedModel.enabled !== false);
  const selectedModelCredit = selectedModelAvailable ? selectedModel?.credit : undefined;

  const handleModelChange = (modelId: string) => {
    setAiModel(modelId);
    const model = findModel(modelId);
    if (model) {
      setCreditCost(parseInt(model.credit, 10));
    }
  };

  // Debounced creator search
  useEffect(() => {
    if (creatorSearch.length < 2) {
      setCreatorResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setCreatorSearching(true);
      try {
        const res = await apiRequest<{ success: boolean; data: { userId: string; name: string; email: string; avatar?: string }[] }>(
          `/api/admin/notifications/search-users?q=${encodeURIComponent(creatorSearch)}`
        );
        if (res.success && res.data) setCreatorResults(res.data);
      } catch (err) {
        console.error('Failed to search users', err);
      } finally {
        setCreatorSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [creatorSearch]);

  const handleChangeCreator = async () => {
    if (!selectedCreator) return;
    setCreatorSaving(true);
    setCreatorSaveMsg(null);
    try {
      const res = await apiRequest<Extension & { success: boolean }>(`/api/admin/extensions/${extension._id}/settings`, {
        method: 'PUT',
        body: JSON.stringify({ Creator: selectedCreator.userId }),
      });
      if (res.success) {
        setCurrentCreatorId(selectedCreator.userId);
        setCurrentCreatorName(selectedCreator.name);
        setCreatorSaveMsg({ type: 'success', text: `Creator changed to ${selectedCreator.name}` });
        setSelectedCreator(null);
        setCreatorSearch('');
        setCreatorResults([]);
        if (onExtensionUpdated) onExtensionUpdated(res as unknown as Extension);
      }
    } catch (err) {
      setCreatorSaveMsg({ type: 'error', text: err instanceof Error ? err.message : 'Failed to change creator' });
    } finally {
      setCreatorSaving(false);
      setTimeout(() => setCreatorSaveMsg(null), 3000);
    }
  };

  const handleSave = async () => {
    const modelChanged = aiModel.trim() !== (savedSettings.aiModel || '');
    const requiresActiveModel = isPublished && !isArchived && (
      savedSettings.isPublished === false ||
      (isPublic && savedSettings.isPublic !== true) ||
      (!isArchived && savedSettings.isArchived === true) ||
      modelChanged
    );
    if (requiresActiveModel && !selectedModelAvailable) {
      setSaveError('Choose an active model before publishing, or save this lens as a draft.');
      return;
    }
    // Mirrors the backend rule: the app's lens page and the follower notification are
    // both built from the first sample, so a live lens must keep at least one.
    if (isPublished && !isArchived && sampleImages.length === 0) {
      setSaveError('Add at least one sample image before publishing, or save this lens as a draft.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      const res = await apiRequest<Extension & { success: boolean }>(`/api/admin/extensions/${extension._id}/settings`, {
        method: 'PUT',
        body: JSON.stringify({
          Name: name, prompt, ...(modelChanged && { aiModel: aiModel.trim() }), CreditCost: creditCost, aspectRatio, official, availableTiers, isDynamicInput, isPublished, isArchived, isPublic,
          description: description.trim() || undefined,
          imageRequirements: imageRequirements.trim() || undefined,
          categories: selectedCategories,
          keywords,
          ReferenceImages: referenceImageKeys,
          ...(samplesDirty && { sampleImages: sampleImages.map(s => ({ inputImages: s.inputImageKeys || s.inputImages, outputImage: s.outputImageKey || s.outputImage })) }),
        }),
      });
      if (res.success) {
        setSavedSettings({ ...savedSettings, aiModel, isPublished, isArchived, isPublic });
        onExtensionUpdated?.(res as unknown as Extension);
      }
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // Suspend / Unsuspend actions
  const handleSuspend = async () => {
    if (!suspendReason.trim()) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await apiRequest(`/api/admin/extensions/${extension._id}/suspend`, {
        method: 'POST',
        body: JSON.stringify({ reason: suspendReason }),
      });
      setSuspendReason('');
      setCurrentReportStatus('Suspended');
      await loadSuspendHistory();
      onStatusChanged?.();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to suspend');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnsuspend = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      await apiRequest(`/api/admin/extensions/${extension._id}/unsuspend`, {
        method: 'POST',
        body: JSON.stringify({ reason: respondReason || 'Unsuspended by admin' }),
      });
      setRespondReason('');
      setCurrentReportStatus('Active');
      await loadSuspendHistory();
      onStatusChanged?.();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to unsuspend');
    } finally {
      setActionLoading(false);
    }
  };

  const handleKeepSuspended = async () => {
    setActionLoading(true);
    setActionError(null);
    try {
      await apiRequest(`/api/admin/extensions/${extension._id}/keep-suspended`, {
        method: 'POST',
        body: JSON.stringify({ reason: respondReason || 'Kept suspended after review' }),
      });
      setRespondReason('');
      setCurrentReportStatus('Suspended');
      await loadSuspendHistory();
      onStatusChanged?.();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to keep suspended');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiRequest(`/api/admin/extensions/${extension._id}`, {
        method: 'DELETE',
      });
      setDeleteConfirmOpen(false);
      onExtensionDeleted?.(extension._id);
      onClose();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Failed to delete lens');
    } finally {
      setDeleting(false);
    }
  };

  // Determine if there's a pending appeal/resubmit that needs admin response
  const lastStatus = statusHistory.length > 0 ? statusHistory[statusHistory.length - 1].status : null;
  const hasPendingAction = lastStatus === 'appeal_pending' || lastStatus === 'resubmit_pending';

  // Category management
  const addCategory = (catId: string) => {
    if (!selectedCategories.includes(catId)) setSelectedCategories([...selectedCategories, catId]);
  };
  const removeCategory = (catId: string) => setSelectedCategories(selectedCategories.filter(c => c !== catId));
  const handleCatDragStart = (idx: number) => setDraggedCatIdx(idx);
  const handleCatDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (draggedCatIdx === null || draggedCatIdx === idx) return;
    const newCats = [...selectedCategories];
    const dragged = newCats[draggedCatIdx];
    newCats.splice(draggedCatIdx, 1);
    newCats.splice(idx, 0, dragged);
    setSelectedCategories(newCats);
    setDraggedCatIdx(idx);
  };
  const handleCatDragEnd = () => setDraggedCatIdx(null);

  // Sample image drag reorder
  const handleImgDragStart = (idx: number) => { setDraggedImageIdx(idx); isDraggingRef.current = true; };
  const handleImgDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (draggedImageIdx === null || draggedImageIdx === idx) return;
    const newImgs = [...sampleImages];
    const dragged = newImgs[draggedImageIdx];
    newImgs.splice(draggedImageIdx, 1);
    newImgs.splice(idx, 0, dragged);
    setSampleImages(newImgs);
    setSamplesDirty(true);
    setDraggedImageIdx(idx);
  };
  const handleImgDragEnd = () => { setDraggedImageIdx(null); setTimeout(() => { isDraggingRef.current = false; }, 0); };
  const isDraggingRef = useRef(false);

  const handleSampleClick = (sample: SampleImage) => {
    if (isDraggingRef.current) return;
    setLightboxSample(sample);
    setLightboxIndex(0);
    setLightboxOpen(true);
  };

  const removeSampleImage = (idx: number) => {
    setSampleImages(prev => prev.filter((_, i) => i !== idx));
    setSamplesDirty(true);
  };

  const openEngagementModal = (type: EngagementType, title: string) => {
    setEngagementModal({ type, title });
  };

  // Reference images: upload one or more (via the admin bridge to the user upload
  // endpoint) and append them. Keys are persisted with the next "Save Changes".
  const handleUploadReference = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = async (ev) => {
      const files = Array.from((ev.target as HTMLInputElement).files || []);
      if (files.length === 0) return;
      setReferenceUploading(true);
      try {
        for (const file of files) {
          const formData = new FormData();
          formData.append('extensionId', extension._id);
          formData.append('image', file);
          const res = await apiUpload<{ success: boolean; s3Key?: string }>(
            '/api/admin/extensions/upload-reference-image',
            formData,
          );
          if (res.success && res.s3Key) {
            const key = res.s3Key;
            // Local preview for instant feedback (persisted on Save)
            const previewUrl = await new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result as string);
              reader.readAsDataURL(file);
            });
            setReferenceImageKeys((prev) => [...prev, key]);
            setReferenceImageUrls((prev) => [...prev, previewUrl]);
          }
        }
      } catch (err) {
        console.error('Failed to upload reference image', err);
      } finally {
        setReferenceUploading(false);
      }
    };
    input.click();
  };

  const handleRemoveReference = (index: number) => {
    setReferenceImageKeys((prev) => prev.filter((_, i) => i !== index));
    setReferenceImageUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddSampleGenerate = async () => {
    if (!selectedModelAvailable) { setAddSampleError('Choose an active model before generating.'); return; }
    if (addSampleInputFiles.length === 0) return;
    setAddSampleGenerating(true);
    setAddSampleGeneratedPreview(null);
    setAddSampleGeneratedKeys(null);
    setAddSampleError(null);

    try {
      const formData = new FormData();
      formData.append('prompt', prompt);
      formData.append('supportsAsync', 'true'); // opt into the async 202 + polling backend path
      if (aiModel) formData.append('model', aiModel);
      if (aspectRatio) formData.append('aspectRatio', aspectRatio);
      // Apply the extension's reference images so the generated sample reflects them
      if (referenceImageKeys.length > 0) formData.append('referenceImageKeys', JSON.stringify(referenceImageKeys));
      addSampleInputFiles.forEach(f => formData.append('images', f));

      // Kick off the async generation — returns a generationId immediately (202).
      const startRes = await apiUpload<GenerationStartResponse>(
        '/api/gemini/transform-with-extension',
        formData,
        GENERATION_START_TIMEOUT_MS,
      );

      if (!startRes.success || !startRes.generationId) {
        throw new Error('Failed to start image generation');
      }

      // Poll until the background generation completes (throws on failure/timeout).
      const generation = await pollGenerationUntilDone(startRes.generationId);

      const imageUrl = generation.enhancedImage?.url;
      if (!imageUrl) throw new Error('No image returned from generation');
      setAddSampleGeneratedPreview(imageUrl);

      // Prefer the keys from the completed record; fall back to the ones returned at start.
      const originalImageKeys = generation.originalImages?.map(img => img.key) ?? startRes.originalImageKeys;
      const enhancedImageKey = generation.enhancedImage?.key;
      if (originalImageKeys && enhancedImageKey) {
        setAddSampleGeneratedKeys({ originalImageKeys, enhancedImageKey });
      }
    } catch (err) {
      setAddSampleError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setAddSampleGenerating(false);
    }
  };

  const handleAcceptAddSample = async () => {
    if (!addSampleGeneratedPreview || !addSampleGeneratedKeys) return;
    setAddSampleSaving(true);
    setAddSampleError(null);
    try {
      const newSample: SampleImage = {
        inputImages: [],  // display URLs will come from reload
        outputImage: '',
        inputImageKeys: addSampleGeneratedKeys.originalImageKeys,
        outputImageKey: addSampleGeneratedKeys.enhancedImageKey,
      };
      const updatedSamples = [...sampleImages, newSample];
      const rawSamples = updatedSamples.map(s => ({
        inputImages: s.inputImageKeys || s.inputImages,
        outputImage: s.outputImageKey || s.outputImage,
      }));
      await apiRequest(`/api/admin/extensions/${extension._id}/settings`, {
        method: 'PUT',
        body: JSON.stringify({ sampleImages: rawSamples }),
      });
      // Reload to get presigned URLs
      const detail = await apiRequest<any>(`/api/admin/extensions/${extension._id}`);
      if (detail.success && detail.sampleImages) {
        setSampleImages(detail.sampleImages);
        setSamplesDirty(false);
      }
      // Reset
      setShowAddSampleModal(false);
      setAddSampleInputFiles([]); setAddSampleInputPreviews([]);
      setAddSampleGeneratedPreview(null); setAddSampleGeneratedKeys(null);
    } catch (err) {
      setAddSampleError(err instanceof Error ? err.message : 'Failed to save sample');
    } finally {
      setAddSampleSaving(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getCategoryLabel = (catId: string) => {
    const cat = allCategories.find(c => c.category_id === catId);
    return cat ? cat.label.en : catId;
  };

  if (!isOpen) return null;

  // Combined status badge: moderation status plus the archived/deleted lifecycle
  // flags, collapsed to a single tag (priority Deleted > Suspended > In Review >
  // Archived > Active). isArchived reflects the live selection in the Details tab.
  const statusBadge = getEffectiveExtensionStatus({
    reportStatus: currentReportStatus,
    isArchived,
    isDeleted: extension.isDeleted,
  });

  return (
    <>
    <div className="ext-modal-overlay" onClick={onClose}>
      <div className="ext-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="ext-modal-header">
          <div className="ext-modal-title-section">
            {extension.thumbnailImageUrl && (
              <img src={extension.thumbnailImageUrl} alt={extension.Name} className="ext-modal-thumbnail" />
            )}
            <div className="ext-modal-title">
              <input
                value={name}
                onChange={e => setName(e.target.value)}
                style={{ fontSize: '20px', fontWeight: 700, color: 'var(--color-text)', background: 'transparent', border: '1px solid transparent', borderRadius: '4px', padding: '2px 6px', margin: '-2px -6px', width: '100%', outline: 'none', transition: 'border-color 0.2s' }}
                onFocus={e => { e.target.style.borderColor = 'var(--color-border-strong)'; }}
                onBlur={e => { e.target.style.borderColor = 'transparent'; }}
              />
              <p className="ext-modal-subtitle">
                by {extension.creatorName || extension.Creator}
                {official && <span className="official-badge">Official</span>}
                <span className="ext-status-badge" style={{ background: statusBadge.color }}>{statusBadge.label}</span>
              </p>
            </div>
          </div>
          <button className="ext-modal-close" onClick={onClose}><FiX /></button>
        </div>

        <div className="ext-modal-tabs">
          <button className={`ext-tab ${activeTab === 'details' ? 'active' : ''}`} onClick={() => setActiveTab('details')}>
            <FiInfo /> Extension Details
          </button>
          <button className={`ext-tab ${activeTab === 'engagement' ? 'active' : ''}`} onClick={() => setActiveTab('engagement')}>
            <FiHeart /> Engagement
          </button>
          <button className={`ext-tab ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => setActiveTab('reports')}>
            <FiShield /> Report & Suspend
            {hasPendingAction && <span className="ext-tab-badge">!</span>}
          </button>
        </div>

        <div className="ext-modal-body">
          {/* ===== DETAILS TAB ===== */}
          {activeTab === 'details' && (
            <div className="ext-edit-form">
              {loading ? (
                <div className="ext-loading">Loading lens details...</div>
              ) : (
                <>
                  <div className="ext-form-section">
                    <label className="ext-form-label">Prompt</label>
                    <textarea className="ext-form-textarea" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={6} placeholder="Enter the lens prompt..." />
                  </div>

                  <div className="ext-form-section">
                    <label className="ext-form-label">Description</label>
                    <textarea className="ext-form-textarea" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} placeholder="Short description of what this lens does..." />
                  </div>

                  <div className="ext-form-section">
                    <label className="ext-form-label">Image Requirements</label>
                    <textarea className="ext-form-textarea" value={imageRequirements} onChange={(e) => setImageRequirements(e.target.value)} rows={2} placeholder="What kind of input images work best?" />
                  </div>

                  <div className="ext-form-row">
                    <div className="ext-form-section flex-1">
                      <label className="ext-form-label">AI Model</label>
                      <div className="ext-form-select-wrap">
                        <select className="ext-form-select" value={aiModel} onChange={(e) => handleModelChange(e.target.value)}>
                          <option value="">Select model...</option>
                          {aiModel && !selectedModel && <option value={aiModel} disabled>{aiModel} (unavailable)</option>}
                          {imageModels.map(m => (
                            <option key={m._id} value={m.model_id} disabled={m.enabled === false}>
                              {m.display_name || m.model_id}{m.enabled === false ? ' (inactive)' : ''}
                            </option>
                          ))}
                        </select>
                        <FiChevronDown className="ext-form-select-chevron" aria-hidden />
                      </div>
                      {selectedModelCredit && (
                        <div className="ext-model-credit">Credit cost: <span>{selectedModelCredit}</span> per generation</div>
                      )}
                      {/* The stored model is no longer in the list (renamed or deleted) — say so
                          instead of showing an apparently unset dropdown. */}
                      {!selectedModelAvailable && (
                        <div className="ext-model-credit" style={{ color: 'var(--color-warning-text)' }}>
                          Choose an active model before generating or publishing. Drafts can still be saved.
                        </div>
                      )}
                    </div>
                    <div className="ext-form-section flex-1">
                      <label className="ext-form-label">Aspect Ratio</label>
                      <div className="ext-form-select-wrap">
                        <select className="ext-form-select" value={aspectRatio} onChange={(e) => setAspectRatio(e.target.value)}>
                          {ASPECT_RATIOS.map(r => (<option key={r} value={r}>{r}</option>))}
                        </select>
                        <FiChevronDown className="ext-form-select-chevron" aria-hidden />
                      </div>
                    </div>
                  </div>

                  <div className="ext-form-section">
                    <label className="ext-form-label">Reference Images ({referenceImageUrls.length})</label>
                    <p className="ext-form-hint">Optional. Sent to the AI ahead of the user's photos to guide style, subject, or character/object consistency — the prompt should refer to them. Add multiple to combine references.</p>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                      {referenceImageUrls.map((url, idx) => (
                        <div key={idx} style={{ position: 'relative', width: '100px', height: '100px' }}>
                          <img src={url} alt={`Reference ${idx + 1}`} style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', border: '2px solid var(--color-border)' }} />
                          <span style={{ position: 'absolute', bottom: '4px', left: '4px', backgroundColor: 'rgba(0,0,0,0.6)', color: 'white', fontSize: '10px', padding: '1px 5px', borderRadius: '4px' }}>#{idx + 1}</span>
                          <button
                            onClick={() => handleRemoveReference(idx)}
                            title="Remove reference image"
                            style={{ position: 'absolute', top: '-6px', right: '-6px', width: '22px', height: '22px', borderRadius: '50%', backgroundColor: 'var(--color-danger-strong)', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', padding: 0 }}
                          >×</button>
                        </div>
                      ))}
                      <button
                        onClick={handleUploadReference}
                        disabled={referenceUploading}
                        style={{ width: '100px', height: '100px', borderRadius: '8px', border: '2px dashed var(--color-border-strong)', backgroundColor: 'var(--color-input)', cursor: referenceUploading ? 'wait' : 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--color-border-strong)', gap: '4px' }}
                      >
                        {referenceUploading ? <span style={{ fontSize: '11px' }}>Uploading…</span> : (<><FiPlus size={24} /><span style={{ fontSize: '11px' }}>Add</span></>)}
                      </button>
                    </div>
                  </div>

                  <div className="ext-form-section">
                    <label className="ext-form-label">Sample Images ({sampleImages.length})</label>
                    <p className="ext-form-hint">Drag to reorder. First image is the cover. Click to preview.</p>
                    {sampleImages.length > 0 ? (
                      <div className="ext-sample-grid">
                        {sampleImages.map((sample, idx) => (
                          <div key={idx} className={`ext-sample-card ${draggedImageIdx === idx ? 'dragging' : ''} ${idx === 0 ? 'cover' : ''}`} draggable onDragStart={() => handleImgDragStart(idx)} onDragOver={(e) => handleImgDragOver(e, idx)} onDragEnd={handleImgDragEnd} onClick={() => handleSampleClick(sample)} style={{ cursor: 'pointer', position: 'relative' }}>
                            <img src={sample.outputImage} alt={`Sample ${idx + 1}`} className="ext-sample-img" />
                            {idx === 0 && <span className="ext-sample-cover-badge">Cover</span>}
                            <span className="ext-sample-index">#{idx + 1}</span>
                            <button
                              onClick={(e) => { e.stopPropagation(); removeSampleImage(idx); }}
                              style={{ position: 'absolute', top: '4px', right: '4px', width: '22px', height: '22px', borderRadius: '50%', backgroundColor: 'rgba(220,38,38,0.85)', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', padding: 0 }}
                              title="Remove sample"
                            >×</button>
                          </div>
                        ))}
                      </div>
                    ) : (<div className="ext-empty-state">No sample images</div>)}

                    {/* Add new sample button */}
                    <button
                      onClick={() => { setShowAddSampleModal(true); setAddSampleInputFiles([]); setAddSampleInputPreviews([]); setAddSampleGeneratedPreview(null); setAddSampleGeneratedKeys(null); setAddSampleError(null); }}
                      style={{ marginTop: '12px', padding: '10px 16px', backgroundColor: 'var(--color-success-strong)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
                    ><FiPlus size={14} /> Add Sample Image</button>

                    {/* Dynamic Input Image toggle */}
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', marginTop: '12px' }}>
                      <input
                        type="checkbox"
                        checked={isDynamicInput}
                        onChange={e => setIsDynamicInput(e.target.checked)}
                        style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: 'var(--color-accent-alt)' }}
                      />
                      <div>
                        <span className="ext-toggle-name">Dynamic number of input images (max 10)</span>
                        <span style={{ fontSize: '12px', color: 'var(--color-muted)', marginTop: '2px', display: 'block' }}>
                          Allow users to choose 1 to 10 input images when using this lens
                        </span>
                      </div>
                    </label>
                  </div>

                  <div className="ext-form-section">
                    <label className="ext-form-label">Access & Visibility</label>
                    <div className="ext-toggles-grid">
                      <label className="ext-toggle-item">
                        <input type="checkbox" checked={official} onChange={(e) => setOfficial(e.target.checked)} />
                        <div className="ext-toggle-info"><span className="ext-toggle-name">Official</span><span className="ext-toggle-desc">Mark as an official lens</span></div>
                      </label>
                      <div className="ext-access-field">
                        <label className="ext-form-sublabel" htmlFor="lens-state">Status</label>
                        <div className="ext-form-select-wrap">
                          <select id="lens-state" className="ext-form-select" value={lensAccessState} onChange={(e) => handleAccessStateChange(e.target.value)} aria-describedby="lens-state-hint">
                            <option value="draft">Draft</option>
                            <option value="private">Private</option>
                            <option value="public">Public</option>
                            <option value="archived">Archived</option>
                          </select>
                          <FiChevronDown className="ext-form-select-chevron" aria-hidden />
                        </div>
                        <span id="lens-state-hint" className="ext-toggle-desc">{LENS_ACCESS_HINTS[lensAccessState]}</span>
                      </div>
                      <div className="ext-form-subsection">
                        <label className="ext-form-sublabel">Available Tiers</label>
                        <p className="ext-form-hint">Select which subscription tiers can access this lens.</p>
                        <div className="ext-tier-checkboxes">
                          {TIER_OPTIONS.map(opt => (
                            <label className="ext-toggle-item" key={opt.value}>
                              <input type="checkbox" checked={availableTiers.includes(opt.value)} onChange={(e) => {
                                if (e.target.checked) setAvailableTiers([...availableTiers, opt.value]);
                                else setAvailableTiers(availableTiers.filter(t => t !== opt.value));
                              }} />
                              <div className="ext-toggle-info"><span className="ext-toggle-name">{opt.label}</span><span className="ext-toggle-desc">{opt.desc}</span></div>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Keywords */}
                  <div className="ext-form-section">
                    <label className="ext-form-label">Keywords</label>
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                      <input type="text" value={keywordInput} onChange={e => setKeywordInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); const kw = keywordInput.trim(); if (kw && !keywords.includes(kw)) { setKeywords([...keywords, kw]); setKeywordInput(''); } } }} placeholder="Type and press Enter" style={{ flex: 1, padding: '8px 12px', border: '1px solid var(--color-border)', borderRadius: '6px', backgroundColor: 'var(--color-input)', color: 'var(--color-text)', fontSize: '14px' }} />
                      <button type="button" onClick={() => { const kw = keywordInput.trim(); if (kw && !keywords.includes(kw)) { setKeywords([...keywords, kw]); setKeywordInput(''); } }} style={{ padding: '8px 12px', backgroundColor: '#00ffff', color: 'var(--color-input)', border: 'none', borderRadius: '6px', cursor: 'pointer' }}><FiPlus size={14} /></button>
                    </div>
                    {keywords.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {keywords.map(kw => (
                          <span key={kw} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', backgroundColor: 'var(--color-surface-hover)', borderRadius: '16px', fontSize: '13px', color: 'var(--color-text)' }}>
                            {kw}
                            <button onClick={() => setKeywords(keywords.filter(k => k !== kw))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontSize: '14px', color: 'var(--color-muted)' }}>×</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="ext-form-section">
                    <label className="ext-form-label">Categories</label>
                    <p className="ext-form-hint">Drag to reorder. Click × to remove.</p>
                    <div className="ext-categories-selected">
                      {selectedCategories.map((catId, idx) => (
                        <div key={catId} className={`ext-cat-chip ${draggedCatIdx === idx ? 'dragging' : ''}`} draggable onDragStart={() => handleCatDragStart(idx)} onDragOver={(e) => handleCatDragOver(e, idx)} onDragEnd={handleCatDragEnd}>
                          <span className="ext-cat-order">{idx + 1}</span>
                          <span>{getCategoryLabel(catId)}</span>
                          <button className="ext-cat-remove" onClick={() => removeCategory(catId)}><FiX /></button>
                        </div>
                      ))}
                    </div>
                    <div className="ext-categories-available">
                      <span className="ext-form-sublabel">Add category:</span>
                      <div className="ext-cat-options">
                        {allCategories.filter(c => !selectedCategories.includes(c.category_id)).map(cat => (
                          <button key={cat._id} className="ext-cat-add-btn" onClick={() => addCategory(cat.category_id)}><FiPlus /> {cat.label.en}</button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="ext-form-section">
                    <label className="ext-form-label"><FiUser style={{ marginRight: 6, verticalAlign: 'middle' }} />Creator</label>
                    <p className="ext-form-hint">Current creator: <strong>{currentCreatorName}</strong> ({currentCreatorId})</p>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        className="ext-form-input"
                        placeholder="Search user by name, email, or ID..."
                        value={selectedCreator ? `${selectedCreator.name} (${selectedCreator.userId})` : creatorSearch}
                        onChange={(e) => {
                          setCreatorSearch(e.target.value);
                          setSelectedCreator(null);
                        }}
                        style={{ width: '100%' }}
                      />
                      {creatorSearching && <span style={{ position: 'absolute', right: 12, top: 10, color: 'var(--color-muted)', fontSize: 12 }}>Searching...</span>}
                      {creatorResults.length > 0 && !selectedCreator && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                          background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '0 0 6px 6px',
                          maxHeight: 200, overflowY: 'auto'
                        }}>
                          {creatorResults.map(u => (
                            <div
                              key={u.userId}
                              onClick={() => {
                                setSelectedCreator({ userId: u.userId, name: u.name });
                                setCreatorResults([]);
                              }}
                              style={{
                                padding: '8px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                                borderBottom: '1px solid var(--color-surface-hover)', fontSize: 13
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-border)')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                            >
                              {u.avatar && <img src={u.avatar} alt="" style={{ width: 24, height: 24, borderRadius: '50%' }} />}
                              <span style={{ color: 'var(--color-text)' }}>{u.name}</span>
                              <span style={{ color: 'var(--color-muted)', fontSize: 11 }}>{u.email}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {selectedCreator && (
                      <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button
                          className="ext-action-btn success"
                          onClick={handleChangeCreator}
                          disabled={creatorSaving}
                          style={{ padding: '6px 16px', fontSize: 13 }}
                        >
                          {creatorSaving ? 'Changing...' : `Change creator to ${selectedCreator.name}`}
                        </button>
                        <button
                          onClick={() => { setSelectedCreator(null); setCreatorSearch(''); }}
                          style={{ padding: '6px 12px', background: 'transparent', color: 'var(--color-muted)', border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                    {creatorSaveMsg && (
                      <div style={{ marginTop: 8, fontSize: 13, color: creatorSaveMsg.type === 'success' ? '#27ae60' : '#e74c3c' }}>
                        {creatorSaveMsg.text}
                      </div>
                    )}
                  </div>

                  <div className="ext-form-section ext-readonly-info">
                    <div className="ext-form-row">
                      <div className="ext-readonly-field"><span className="ext-readonly-label">Lens ID</span><span className="ext-readonly-value mono">{extension._id}</span></div>
                      <div className="ext-readonly-field"><span className="ext-readonly-label">Created</span><span className="ext-readonly-value">{formatDate(extension.createdAt)}</span></div>
                      <div className="ext-readonly-field"><span className="ext-readonly-label">Updated</span><span className="ext-readonly-value">{formatDate(extension.updatedAt)}</span></div>
                    </div>
                  </div>

                  <div className="ext-save-bar">
                    {saveError && <span className="ext-save-error">{saveError}</span>}
                    {saveSuccess && <span className="ext-save-success">Saved successfully</span>}
                    <button className="ext-save-btn" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
                  </div>

                  {/* Danger Zone - Delete */}
                  <div className="ext-form-section" style={{ marginTop: '24px', padding: '16px', border: '1px solid #e74c3c40', borderRadius: '8px', backgroundColor: '#1a0a0a' }}>
                    <label className="ext-form-label" style={{ color: '#e74c3c' }}>Danger Zone</label>
                    {!deleteConfirmOpen ? (
                      <button
                        onClick={() => setDeleteConfirmOpen(true)}
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '6px',
                          padding: '8px 16px', backgroundColor: 'transparent', color: '#e74c3c',
                          border: '1px solid #e74c3c', borderRadius: '6px', cursor: 'pointer', fontSize: '14px'
                        }}
                      >
                        <FiTrash2 /> Delete Extension
                      </button>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <p style={{ color: 'var(--color-danger)', margin: 0, fontSize: '14px' }}>
                          Are you sure? This will permanently delete this lens and all associated data. This action cannot be undone.
                        </p>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={handleDelete}
                            disabled={deleting}
                            style={{
                              padding: '8px 16px', backgroundColor: '#e74c3c', color: 'white',
                              border: 'none', borderRadius: '6px', cursor: deleting ? 'not-allowed' : 'pointer', fontSize: '14px'
                            }}
                          >
                            {deleting ? 'Deleting...' : 'Yes, Delete'}
                          </button>
                          <button
                            onClick={() => setDeleteConfirmOpen(false)}
                            style={{
                              padding: '8px 16px', backgroundColor: 'transparent', color: 'var(--color-muted)',
                              border: '1px solid var(--color-border)', borderRadius: '6px', cursor: 'pointer', fontSize: '14px'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ===== ENGAGEMENT TAB ===== */}
          {activeTab === 'engagement' && (
            <div className="ext-engagement-grid">
              <button
                type="button"
                className="engagement-card likes clickable"
                onClick={() => openEngagementModal('liked', 'Liked by')}
                title="View users who liked this lens"
              >
                <div className="engagement-icon"><FiHeart /></div>
                <div className="engagement-stats"><div className="engagement-count">{extension.likeCount}</div><div className="engagement-label">Likes</div></div>
              </button>
              <button
                type="button"
                className="engagement-card bookmarks clickable"
                onClick={() => openEngagementModal('bookmarked', 'Bookmarked by')}
                title="View users who bookmarked this lens"
              >
                <div className="engagement-icon"><FiBookmark /></div>
                <div className="engagement-stats"><div className="engagement-count">{extension.bookmarkCount}</div><div className="engagement-label">Bookmarks</div></div>
              </button>
              <button
                type="button"
                className="engagement-card usage clickable"
                onClick={() => openEngagementModal('used', 'Used by')}
                title="View users who used this lens"
              >
                <div className="engagement-icon"><FiZap /></div>
                <div className="engagement-stats"><div className="engagement-count">{usageCount === null ? '…' : usageCount.toLocaleString()}</div><div className="engagement-label">Usages</div></div>
              </button>
              <div className="ext-info-card full-width">
                <h3>Engagement Overview</h3>
                <p className="engagement-description">
                  This lens has been liked by {extension.likeCount} users and bookmarked by {extension.bookmarkCount} users
                  {usageCount !== null && `, and used ${usageCount.toLocaleString()} time${usageCount === 1 ? '' : 's'}`}.
                  {' '}Tap Likes, Bookmarks or Usages above to see who.
                </p>
              </div>
            </div>
          )}

          {/* ===== REPORT & SUSPEND TAB ===== */}
          {activeTab === 'reports' && (
            <div className="ext-report-tab">
              {/* Current status + action area */}
              <div className="ext-report-status-bar">
                <div className="ext-report-current">
                  <span className="ext-report-status-dot" style={{ background: statusBadge.color }} />
                  <span className="ext-report-status-label">Current Status: <strong style={{ color: statusBadge.color }}>{statusBadge.label}</strong></span>
                </div>
                {!reportsLoading && (
                  <span className="ext-report-status-label">
                    User Reports: <strong style={{ color: totalReports > 0 ? '#e74c3c' : '#27ae60' }}>{totalReports}</strong>
                  </span>
                )}
              </div>

              {/* User reports: who reported this extension and why */}
              <div className="ext-timeline-section">
                <h3 className="ext-timeline-title">User Reports{!reportsLoading ? ` (${totalReports})` : ''}</h3>
                {reportsLoading ? (
                  <div className="ext-loading">Loading reports...</div>
                ) : totalReports === 0 ? (
                  <div className="ext-empty-state">No user reports for this lens.</div>
                ) : (
                  <>
                    {/* Reason breakdown */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                      {Object.entries(reasonBreakdown)
                        .sort((a, b) => b[1] - a[1])
                        .map(([reason, count]) => (
                          <span key={reason} style={{ padding: '3px 10px', borderRadius: '12px', background: '#1e1e1e', border: '1px solid #444', color: 'var(--color-text-secondary)', fontSize: '12px' }}>
                            {reportReasonLabel(reason)} · {count}
                          </span>
                        ))}
                    </div>

                    {/* Individual reports */}
                    {reports.length < totalReports && (
                      <div style={{ fontSize: '12px', color: 'var(--color-muted)', marginBottom: '10px' }}>
                        Showing {reports.length} of {totalReports} reports
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {reports.map((r) => (
                        <div key={r._id} style={{ background: '#1e1e1e', border: '1px solid #444', borderRadius: '8px', padding: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{ padding: '2px 8px', borderRadius: '10px', background: '#1c2b3a', color: 'var(--color-accent)', fontSize: '11px', fontWeight: 600 }}>
                                {reportReasonLabel(r.reason)}
                              </span>
                              <span style={{ color: '#e0e0e0', fontSize: '13px', fontWeight: 500 }}>{r.reporterName}</span>
                              {r.reporterEmail && <span style={{ color: 'var(--color-subtle)', fontSize: '12px' }}>{r.reporterEmail}</span>}
                            </span>
                            <span style={{ color: 'var(--color-subtle)', fontSize: '12px' }}>{formatDate(r.createdAt)}</span>
                          </div>
                          {r.additionalInfo && (
                            <div style={{ marginTop: '8px', color: 'var(--color-muted)', fontSize: '13px', whiteSpace: 'pre-wrap' }}>{r.additionalInfo}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Admin action area */}
              {actionError && <div className="ext-action-error">{actionError}</div>}

              {currentReportStatus === 'Active' && (
                <div className="ext-action-card suspend-action">
                  <h3><FiAlertCircle /> Suspend Lens</h3>
                  <p className="ext-action-desc">Suspending will hide this lens from all users and notify the creator.</p>
                  <textarea
                    className="ext-form-textarea"
                    value={suspendReason}
                    onChange={(e) => setSuspendReason(e.target.value)}
                    rows={3}
                    placeholder="Enter suspension reason (required)..."
                  />
                  <button className="ext-action-btn danger" onClick={handleSuspend} disabled={actionLoading || !suspendReason.trim()}>
                    {actionLoading ? 'Suspending...' : 'Suspend Extension'}
                  </button>
                </div>
              )}

              {(currentReportStatus === 'Suspended' || currentReportStatus === 'inReview') && !hasPendingAction && (
                <div className="ext-action-card unsuspend-action">
                  <h3><FiCheckCircle /> Unsuspend Lens</h3>
                  <p className="ext-action-desc">This will restore the lens and make it visible to users again.</p>
                  <textarea
                    className="ext-form-textarea"
                    value={respondReason}
                    onChange={(e) => setRespondReason(e.target.value)}
                    rows={2}
                    placeholder="Optional reason..."
                  />
                  <button className="ext-action-btn success" onClick={handleUnsuspend} disabled={actionLoading}>
                    {actionLoading ? 'Processing...' : 'Unsuspend Extension'}
                  </button>
                </div>
              )}

              {hasPendingAction && (
                <div className="ext-action-card review-action">
                  <h3><FiMessageSquare /> {lastStatus === 'appeal_pending' ? 'Appeal Pending Review' : 'Resubmission Pending Review'}</h3>
                  <p className="ext-action-desc">
                    {lastStatus === 'appeal_pending'
                      ? 'The creator has submitted an appeal. Review and respond below.'
                      : 'The creator has modified the lens and resubmitted for review.'}
                  </p>
                  {statusHistory.length > 0 && statusHistory[statusHistory.length - 1].reason && (
                    <div className="ext-appeal-message">
                      <span className="ext-appeal-label">Creator's message:</span>
                      <p>{statusHistory[statusHistory.length - 1].reason}</p>
                    </div>
                  )}
                  <textarea
                    className="ext-form-textarea"
                    value={respondReason}
                    onChange={(e) => setRespondReason(e.target.value)}
                    rows={2}
                    placeholder="Response reason (optional)..."
                  />
                  <div className="ext-action-btn-row">
                    <button className="ext-action-btn success" onClick={handleUnsuspend} disabled={actionLoading}>
                      {actionLoading ? 'Processing...' : 'Approve & Unsuspend'}
                    </button>
                    <button className="ext-action-btn danger" onClick={handleKeepSuspended} disabled={actionLoading}>
                      {actionLoading ? 'Processing...' : 'Reject & Keep Suspended'}
                    </button>
                  </div>
                </div>
              )}

              {/* Vertical Timeline */}
              <div className="ext-timeline-section">
                <h3 className="ext-timeline-title">Status History</h3>
                {historyLoading ? (
                  <div className="ext-loading">Loading history...</div>
                ) : statusHistory.length === 0 ? (
                  <div className="ext-empty-state">No suspension history for this lens.</div>
                ) : (
                  <div className="ext-timeline">
                    {[...statusHistory].reverse().map((item, idx) => {
                      const config = STATUS_CONFIG[item.status] || { label: item.status, color: 'var(--color-subtle)', icon: <FiClock />, bgColor: '#2a2a2a' };
                      return (
                        <div key={idx} className="ext-timeline-item">
                          <div className="ext-timeline-line-container">
                            <div className="ext-timeline-dot" style={{ background: config.color, boxShadow: `0 0 8px color-mix(in srgb, ${config.color} 25%, transparent)` }}>
                              {config.icon}
                            </div>
                            {idx < statusHistory.length - 1 && <div className="ext-timeline-line" />}
                          </div>
                          <div className="ext-timeline-content" style={{ borderLeftColor: config.color }}>
                            <div className="ext-timeline-header">
                              <span className="ext-timeline-status" style={{ color: config.color }}>{config.label}</span>
                              <span className="ext-timeline-time">{formatDate(item.time)}</span>
                            </div>
                            {item.reason && (
                              <p className="ext-timeline-reason">{item.reason}</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

    {/* Add Sample Generate Modal */}
    {showAddSampleModal && createPortal(
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 5000, padding: '20px',
      }} onClick={() => { if (!addSampleGenerating && !addSampleSaving) setShowAddSampleModal(false); }}>
        {/* Card shell — `position: relative` anchors the generating overlay below so it
            covers the whole card (header included) instead of scrolling with the body. */}
        <div style={{
          position: 'relative', width: '520px', maxWidth: '90vw',
          backgroundColor: 'var(--color-surface-secondary)', borderRadius: '12px',
          border: '1px solid var(--color-border)', boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
        }} onClick={e => e.stopPropagation()}>
          <div style={{ padding: '24px', maxHeight: '85vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: 'var(--color-text)', fontSize: '18px' }}>Generate New Sample</h3>
              {/* Closing is blocked while a generation/save is in flight (same guard as the
                  backdrop click) — the job keeps running server-side and has spent credits. */}
              <button
                onClick={() => { if (!addSampleGenerating && !addSampleSaving) setShowAddSampleModal(false); }}
                disabled={addSampleGenerating || addSampleSaving}
                style={{
                  background: 'none', border: 'none', color: 'var(--color-muted)', fontSize: '20px', padding: '4px',
                  cursor: addSampleGenerating || addSampleSaving ? 'not-allowed' : 'pointer',
                  opacity: addSampleGenerating || addSampleSaving ? 0.5 : 1,
                }}
              >✕</button>
            </div>

            {/* Image slots */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontSize: '13px', color: 'var(--color-muted)', display: 'block', marginBottom: '8px' }}>
                Input Images ({addSampleInputFiles.filter(Boolean).length}/{isDynamicInput ? '10' : (extension.requiredPhotoCount || 1)})
              </label>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                {isDynamicInput ? (
                  <>
                    {addSampleInputPreviews.map((preview, slotIdx) => (
                      <div key={slotIdx} style={{ position: 'relative', width: '100px', height: '100px' }}>
                        <img src={preview} alt={`Input ${slotIdx + 1}`} style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', border: '2px solid var(--color-border)' }} />
                        <button onClick={() => {
                          setAddSampleInputFiles(prev => prev.filter((_, i) => i !== slotIdx));
                          setAddSampleInputPreviews(prev => prev.filter((_, i) => i !== slotIdx));
                        }} style={{ position: 'absolute', top: '-6px', right: '-6px', width: '22px', height: '22px', borderRadius: '50%', backgroundColor: 'var(--color-danger-strong)', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', padding: 0 }}>×</button>
                      </div>
                    ))}
                    {addSampleInputFiles.filter(Boolean).length < 10 && (
                      <button onClick={() => {
                        const input = document.createElement('input');
                        input.type = 'file'; input.accept = 'image/*'; input.multiple = true;
                        input.onchange = (ev) => {
                          const files = Array.from((ev.target as HTMLInputElement).files || []);
                          if (files.length === 0) return;
                          const remaining = 10 - addSampleInputFiles.filter(Boolean).length;
                          const toAdd = files.slice(0, remaining);
                          setAddSampleInputFiles(prev => [...prev, ...toAdd]);
                          toAdd.forEach(file => {
                            const reader = new FileReader();
                            reader.onloadend = () => setAddSampleInputPreviews(prev => [...prev, reader.result as string]);
                            reader.readAsDataURL(file);
                          });
                        };
                        input.click();
                      }} style={{
                        width: '100px', height: '100px', borderRadius: '8px', border: '2px dashed var(--color-border-strong)',
                        backgroundColor: 'var(--color-input)', cursor: 'pointer', display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', color: 'var(--color-border-strong)', gap: '4px',
                      }}>
                        <FiPlus size={24} />
                        <span style={{ fontSize: '11px' }}>Add</span>
                      </button>
                    )}
                  </>
                ) : (
                Array.from({ length: extension.requiredPhotoCount || 1 }).map((_, slotIdx) => {
                  const preview = addSampleInputPreviews[slotIdx];
                  return (
                    <div key={slotIdx} style={{ position: 'relative' }}>
                      {preview ? (
                        <div style={{ position: 'relative', width: '100px', height: '100px' }}>
                          <img src={preview} alt={`Input ${slotIdx + 1}`} style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', border: '2px solid var(--color-border)' }} />
                          <button onClick={() => {
                            setAddSampleInputFiles(prev => prev.filter((_, i) => i !== slotIdx));
                            setAddSampleInputPreviews(prev => prev.filter((_, i) => i !== slotIdx));
                          }} style={{ position: 'absolute', top: '-6px', right: '-6px', width: '22px', height: '22px', borderRadius: '50%', backgroundColor: 'var(--color-danger-strong)', color: 'white', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', padding: 0 }}>×</button>
                        </div>
                      ) : (
                        <button onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file'; input.accept = 'image/*';
                          input.onchange = (ev) => {
                            const file = (ev.target as HTMLInputElement).files?.[0];
                            if (!file) return;
                            setAddSampleInputFiles(prev => { const next = [...prev]; next[slotIdx] = file; return next; });
                            const reader = new FileReader();
                            reader.onloadend = () => setAddSampleInputPreviews(prev => { const next = [...prev]; next[slotIdx] = reader.result as string; return next; });
                            reader.readAsDataURL(file);
                          };
                          input.click();
                        }} style={{
                          width: '100px', height: '100px', borderRadius: '8px', border: '2px dashed var(--color-border-strong)',
                          backgroundColor: 'var(--color-input)', cursor: 'pointer', display: 'flex', flexDirection: 'column',
                          alignItems: 'center', justifyContent: 'center', color: 'var(--color-border-strong)', gap: '4px',
                        }}>
                          <FiPlus size={24} />
                          <span style={{ fontSize: '11px' }}>Image {slotIdx + 1}</span>
                        </button>
                      )}
                    </div>
                  );
                })
                )}
              </div>
            </div>

            {/* Generate button */}
            {!addSampleGeneratedPreview && (
              <button
                onClick={handleAddSampleGenerate}
                disabled={!selectedModelAvailable || addSampleInputFiles.filter(Boolean).length === 0 || addSampleGenerating}
                style={{
                  padding: '12px 20px', width: '100%', marginBottom: '16px',
                  backgroundColor: addSampleInputFiles.filter(Boolean).length > 0 ? '#00ffff' : 'var(--color-surface-hover)',
                  color: addSampleInputFiles.filter(Boolean).length > 0 ? 'var(--color-input)' : 'var(--color-muted)', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 500,
                  cursor: addSampleInputFiles.filter(Boolean).length > 0 ? 'pointer' : 'not-allowed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                }}
              >
                {`Generate Preview (${selectedModelCredit || '?'} credits)`}
              </button>
            )}

            {/* Error */}
            {addSampleError && <p style={{ color: 'var(--color-danger)', fontSize: '13px', marginBottom: '12px', padding: '8px 12px', backgroundColor: 'var(--color-danger-surface)', borderRadius: '6px' }}>{addSampleError}</p>}

            {/* Generated preview */}
            {addSampleGeneratedPreview && (
              <div>
                <label style={{ fontSize: '13px', color: 'var(--color-muted)', display: 'block', marginBottom: '8px' }}>Generated Result</label>
                <img src={addSampleGeneratedPreview} alt="Generated" style={{ width: '100%', borderRadius: '8px', border: '1px solid var(--color-border)' }} />
                <div style={{ display: 'flex', gap: '10px', marginTop: '14px' }}>
                  <button
                    onClick={handleAcceptAddSample}
                    disabled={addSampleSaving}
                    style={{ flex: 1, padding: '12px', backgroundColor: 'var(--color-success-strong)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}
                  >{addSampleSaving ? 'Saving...' : 'Accept & Add'}</button>
                  <button
                    onClick={() => { setAddSampleGeneratedPreview(null); setAddSampleGeneratedKeys(null); handleAddSampleGenerate(); }}
                    disabled={addSampleGenerating}
                    style={{ flex: 1, padding: '12px', backgroundColor: 'var(--color-border)', color: 'var(--color-text)', border: '1px solid var(--color-border-strong)', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}
                  >{addSampleGenerating ? 'Generating...' : 'Regenerate'}</button>
                </div>
              </div>
            )}
          </div>

          {/* Generating overlay — covers the whole card (header, inputs, buttons) so nothing
              is clickable and the modal can't be dismissed while the job is in flight. */}
          {addSampleGenerating && (
            <div style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: '12px',
              backgroundColor: 'rgba(13,17,23,0.92)', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: '14px', padding: '24px', textAlign: 'center',
            }}>
              <div style={{ width: '44px', height: '44px', border: '3px solid var(--color-border)', borderTopColor: '#00ffff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
              <span style={{ color: 'var(--color-text)', fontSize: '15px', fontWeight: 500 }}>Generating image...</span>
              <span style={{ color: 'var(--color-muted)', fontSize: '13px' }}>This can take a few minutes — keep this window open.</span>
            </div>
          )}
        </div>
      </div>,
      document.body
    )}

    {/* Sample Image Lightbox */}
    {lightboxOpen && lightboxSample && (() => {
      const photos = buildLightboxPhotos(lightboxSample);
      if (photos.length === 0) return null;
      const idx = Math.min(lightboxIndex, photos.length - 1);
      const current = photos[idx];
      const ENHANCED_ACCENT = '#a371f7';
      const ACTIVE_ACCENT = 'var(--color-accent)';
      const navBtnStyle: React.CSSProperties = {
        position: 'absolute', top: '50%', transform: 'translateY(-50%)',
        background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.35)',
        color: 'white', fontSize: '30px', lineHeight: 1, width: '44px', height: '44px', borderRadius: '50%',
        cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, zIndex: 2,
      };
      return createPortal(
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.92)', display: 'flex', justifyContent: 'center', alignItems: 'center',
            zIndex: 9999, padding: '20px', animation: 'lightboxFadeIn 0.2s ease-in-out',
          }}
          onClick={() => setLightboxOpen(false)}
        >
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', alignItems: 'center', animation: 'lightboxSlideUp 0.3s ease-out' }} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setLightboxOpen(false)} style={{
              position: 'absolute', top: '-50px', right: 0,
              background: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.5)',
              color: 'white', fontSize: '28px', width: '44px', height: '44px', borderRadius: '50%',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
              transition: 'background-color 0.2s, transform 0.2s',
            }}>✕</button>

            {/* Main image with prev/next navigation */}
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', maxWidth: '90vw' }}>
              {photos.length > 1 && (
                <button
                  onClick={() => setLightboxIndex((idx - 1 + photos.length) % photos.length)}
                  style={{ ...navBtnStyle, left: '10px' }}
                  aria-label="Previous image"
                >‹</button>
              )}
              <img
                src={current.url}
                alt={current.label}
                style={{
                  maxWidth: '100%', maxHeight: 'calc(90vh - 220px)', objectFit: 'contain',
                  borderRadius: '8px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', userSelect: 'none',
                  border: `3px solid ${current.isEnhanced ? ENHANCED_ACCENT : 'transparent'}`,
                }}
                draggable={false}
              />
              {photos.length > 1 && (
                <button
                  onClick={() => setLightboxIndex((idx + 1) % photos.length)}
                  style={{ ...navBtnStyle, right: '10px' }}
                  aria-label="Next image"
                >›</button>
              )}
            </div>

            {/* Caption */}
            <div style={{
              marginTop: '14px', color: 'white', fontSize: '15px', fontWeight: 600, textAlign: 'center',
              background: 'rgba(0,0,0,0.6)', padding: '9px 20px', borderRadius: '20px',
              display: 'flex', alignItems: 'center', gap: '8px',
              border: `1px solid ${current.isEnhanced ? ENHANCED_ACCENT : 'rgba(255,255,255,0.15)'}`,
            }}>
              {current.isEnhanced
                ? <><FaMagic style={{ color: ENHANCED_ACCENT }} /><span style={{ color: ENHANCED_ACCENT }}>Enhanced Result</span></>
                : <><FaCamera /><span>{current.label}</span></>}
            </div>

            {/* Thumbnail strip */}
            {photos.length > 1 && (
              <div style={{ display: 'flex', gap: '10px', marginTop: '16px', maxWidth: '90vw', overflowX: 'auto', padding: '4px 2px' }}>
                {photos.map((p, i) => {
                  const isActive = i === idx;
                  const accent = p.isEnhanced ? ENHANCED_ACCENT : ACTIVE_ACCENT;
                  return (
                    <button
                      key={i}
                      onClick={() => setLightboxIndex(i)}
                      title={p.isEnhanced ? 'Enhanced result' : p.label}
                      style={{
                        flex: '0 0 auto', position: 'relative', padding: 0, cursor: 'pointer',
                        background: 'var(--color-surface-secondary)', borderRadius: '8px', overflow: 'hidden',
                        border: `2px solid ${isActive ? accent : (p.isEnhanced ? 'rgba(163,113,247,0.5)' : 'var(--color-border)')}`,
                        boxShadow: isActive ? `0 0 0 2px color-mix(in srgb, ${accent} 33%, transparent)` : 'none',
                        transition: 'border-color 0.15s, box-shadow 0.15s', width: '82px',
                      }}
                    >
                      <img src={p.url} alt={p.label} draggable={false} style={{ display: 'block', width: '82px', height: '82px', objectFit: 'cover', opacity: isActive ? 1 : 0.7 }} />
                      {p.isEnhanced && (
                        <span style={{
                          position: 'absolute', top: '3px', left: '3px', fontSize: '11px', lineHeight: 1,
                          background: ENHANCED_ACCENT, color: 'white', borderRadius: '4px', padding: '2px 4px', fontWeight: 700,
                        }}><FaMagic /></span>
                      )}
                      <span style={{
                        display: 'block', fontSize: '10px', fontWeight: 600, textAlign: 'center', padding: '3px 2px',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        color: p.isEnhanced ? ENHANCED_ACCENT : (isActive ? 'var(--color-text)' : 'var(--color-muted)'),
                        background: isActive ? 'rgba(255,255,255,0.06)' : 'transparent',
                      }}>{p.isEnhanced ? 'Enhanced' : p.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>,
        document.body
      );
    })()}

    {/* Engagement users sub-modal (likes / bookmarks / usages) */}
    {engagementModal && createPortal(
      <div
        className="engagement-users-overlay"
        onClick={() => setEngagementModal(null)}
      >
        <div
          role="dialog"
          ref={engagementDialogRef}
          aria-modal="true"
          aria-labelledby="engagement-users-title"
          className="engagement-users-dialog"
          onClick={e => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--color-border)' }}>
            <h3 id="engagement-users-title" style={{ margin: 0, fontSize: '16px', color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              {engagementModal.type === 'liked' ? <FiHeart /> : engagementModal.type === 'used' ? <FiZap /> : <FiBookmark />}
              {engagementModal.title}
              {engagementFeed.response && <span style={{ color: 'var(--color-muted)', fontWeight: 400 }}>({engagementTotal.toLocaleString()})</span>}
            </h3>
            <button type="button" aria-label="Close user list" onClick={() => setEngagementModal(null)} style={{ background: 'none', border: 'none', color: 'var(--color-muted)', cursor: 'pointer', fontSize: '18px', display: 'flex' }}><FiX /></button>
          </div>
          {engagementModal.type === 'used' && (
            <p style={{ margin: 0, padding: '12px 20px 0', fontSize: '12px', color: 'var(--color-muted)' }}>
              Unique users · successful uses only. Repeat uses count separately.
            </p>
          )}
          <div className="engagement-users-scroll" ref={engagementScrollRef} aria-busy={engagementLoading}>
            {!engagementLoading && !engagementError && engagementUsers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px', color: 'var(--color-muted)' }}>
                {engagementModal.type === 'used' ? 'No users have used this lens yet.' : 'No users found.'}
              </div>
            ) : (
              engagementUsers.map(u => (
                <div key={u.userId} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid var(--color-surface-hover)' }}>
                  {u.profilePicture ? (
                    <img src={u.profilePicture} alt="" style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: 'var(--color-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-muted)', fontSize: '14px', fontWeight: 600, flexShrink: 0 }}>
                      {(u.name || '?')[0].toUpperCase()}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--color-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{u.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--color-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {u.email || u.userId}
                      {u.plan ? ` · ${PLAN_LABELS[u.plan] ?? 'Free'}` : ''}
                      {u.isCreator ? ' · Creator' : ''}
                    </div>
                  </div>
                  {engagementModal.type === 'used' && u.usageCount !== undefined && (
                    <span style={{ fontSize: '12px', color: 'var(--color-text)', whiteSpace: 'nowrap' }}>
                      {u.usageCount.toLocaleString()} {u.usageCount === 1 ? 'use' : 'uses'}
                    </span>
                  )}
                </div>
              ))
            )}
            <LazyListFooter rootRef={engagementScrollRef} count={engagementUsers.length} total={engagementFeed.response ? engagementTotal : undefined}
              noun="users" loading={engagementLoading} error={engagementError} hasMore={engagementFeed.hasMore}
              onLoadMore={engagementFeed.loadMore} onRetry={engagementFeed.retry} />
          </div>
        </div>
      </div>,
      document.body
    )}
    </>
  );
}
